import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CHAIN_OPTIONS = [
  { id: 'solana', label: 'Solana', color: 'bg-purple-100 text-purple-800', hint: null },
  {
    id: 'evm',
    label: 'EVM',
    color: 'bg-blue-100 text-blue-800 dark:text-blue-300',
    hint: 'One address for Base · Ethereum · BSC · Robinhood',
  },
];

const EVM_NETWORK_LABELS = ['Base', 'Ethereum', 'BSC', 'Robinhood'];
const CHAIN_LABEL = {
  solana: 'Solana',
  evm: 'EVM',
  ethereum: 'Ethereum',
  base: 'Base',
  bsc: 'BSC',
  robinhood: 'Robinhood',
};
const CHAIN_COLOR = {
  solana: 'bg-purple-100 text-purple-800',
  evm: 'bg-blue-100 text-blue-800 dark:text-blue-300',
  ethereum: 'bg-indigo-100 text-indigo-800',
  base: 'bg-blue-100 text-blue-800 dark:text-blue-300',
  bsc: 'bg-yellow-100 text-yellow-800 dark:text-yellow-300',
  robinhood: 'bg-green-100 text-green-800 dark:text-green-300',
};

export default function TradingWallets() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ label: '', chains: [], notes: '' });
  const [creating, setCreating] = useState(false);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importForm, setImportForm] = useState({
    label: '',
    chains: [],
    notes: '',
    solanaPrivateKey: '',
    evmPrivateKey: '',
  });
  const [importing, setImporting] = useState(false);

  // Detail modal
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ label: '', notes: '' });
  const [editing, setEditing] = useState(false);

  // Portfolio data
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  // Export key modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportChain, setExportChain] = useState('');
  const [exportedKey, setExportedKey] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const response = await api.get('/trading/wallets');
      setWallets(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch trading wallets:', err);
      setError('Failed to load trading wallets');
    } finally {
      setLoading(false);
    }
  };

  const createWallet = async () => {
    if (!createForm.label.trim()) return alert('Label is required');
    if (createForm.chains.length === 0) return alert('Select at least one chain');

    setCreating(true);
    try {
      await api.post('/trading/wallets', createForm);
      setShowCreateModal(false);
      setCreateForm({ label: '', chains: [], notes: '' });
      fetchWallets();
    } catch (err) {
      console.error('Failed to create wallet:', err);
      alert(err.response?.data?.error || 'Failed to create wallet');
    } finally {
      setCreating(false);
    }
  };

  const importWallet = async () => {
    if (!importForm.label.trim()) return alert('Label is required');
    if (importForm.chains.length === 0) return alert('Select at least one chain');

    const needsSolana = importForm.chains.includes('solana');
    const needsEvm = importForm.chains.includes('evm');
    if (needsSolana && !importForm.solanaPrivateKey.trim()) {
      return alert('Solana private key is required');
    }
    if (needsEvm && !importForm.evmPrivateKey.trim()) {
      return alert('EVM private key is required');
    }

    setImporting(true);
    try {
      await api.post('/trading/wallets/import', {
        label: importForm.label,
        chains: importForm.chains,
        notes: importForm.notes,
        solanaPrivateKey: needsSolana ? importForm.solanaPrivateKey.trim() : undefined,
        evmPrivateKey: needsEvm ? importForm.evmPrivateKey.trim() : undefined,
      });
      setShowImportModal(false);
      setImportForm({ label: '', chains: [], notes: '', solanaPrivateKey: '', evmPrivateKey: '' });
      fetchWallets();
    } catch (err) {
      console.error('Failed to import wallet:', err);
      alert(err.response?.data?.error || 'Failed to import wallet');
    } finally {
      setImporting(false);
    }
  };

  const toggleImportChain = (chainId) => {
    setImportForm((prev) => ({
      ...prev,
      chains: prev.chains.includes(chainId)
        ? prev.chains.filter((c) => c !== chainId)
        : [...prev.chains, chainId],
    }));
  };

  const viewWallet = async (id) => {
    try {
      const response = await api.get(`/trading/wallets/${id}`);
      setSelectedWallet(response.data);
      setShowDetailModal(true);
      // Fetch portfolio data
      setPortfolioLoading(true);
      try {
        const pRes = await api.get(`/trading/wallets/${id}/portfolio`);
        setPortfolio(pRes.data);
      } catch {
        setPortfolio(null);
      } finally {
        setPortfolioLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
      alert('Failed to load wallet details');
    }
  };

  const openEditModal = () => {
    setEditForm({ label: selectedWallet.label, notes: selectedWallet.notes || '' });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    setEditing(true);
    try {
      await api.put(`/trading/wallets/${selectedWallet._id}`, editForm);
      setShowEditModal(false);
      fetchWallets();
      viewWallet(selectedWallet._id);
    } catch (err) {
      console.error('Failed to update wallet:', err);
      alert(err.response?.data?.error || 'Failed to update wallet');
    } finally {
      setEditing(false);
    }
  };

  const deleteWallet = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this wallet?')) return;
    try {
      await api.delete(`/trading/wallets/${id}`);
      setShowDetailModal(false);
      setSelectedWallet(null);
      fetchWallets();
    } catch (err) {
      console.error('Failed to delete wallet:', err);
      alert('Failed to deactivate wallet');
    }
  };

  const toggleWalletStatus = async (id) => {
    try {
      const response = await api.put(`/trading/wallets/${id}/toggle`);
      const updated = response.data;
      alert(`Wallet ${updated.isActive ? 'enabled' : 'disabled'} successfully`);
      fetchWallets();
      if (selectedWallet && selectedWallet._id === id) {
        setSelectedWallet(updated);
      }
    } catch (err) {
      console.error('Failed to toggle wallet:', err);
      alert('Failed to toggle wallet status');
    }
  };

  const openExportModal = (chain) => {
    setExportChain(chain);
    setExportedKey('');
    setShowExportModal(true);
  };

  const exportKey = async () => {
    setExporting(true);
    try {
      const response = await api.post(`/trading/wallets/${selectedWallet._id}/export-key`, {
        chain: exportChain,
      });
      setExportedKey(response.data.privateKey);
    } catch (err) {
      console.error('Failed to export key:', err);
      alert(err.response?.data?.error || 'Failed to export private key');
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard');
  };

  const truncateAddress = (addr) => {
    if (!addr) return 'N/A';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getChainBadge = (chainId) => CHAIN_COLOR[chainId] || 'bg-gray-100 text-gray-800 dark:text-gray-100';

  const getChainLabel = (chainId) => CHAIN_LABEL[chainId] || chainId;

  const toggleChain = (chainId) => {
    setCreateForm((prev) => ({
      ...prev,
      chains: prev.chains.includes(chainId)
        ? prev.chains.filter((c) => c !== chainId)
        : [...prev.chains, chainId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading trading wallets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Trading Wallets</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            ⇩ Import Wallet
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Create Wallet
          </button>
        </div>
      </div>

      {/* Wallets Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Label
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Chains
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Addresses
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {wallets.filter(w => w.isActive !== false).length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No active trading wallets. Create one to get started.
                </td>
              </tr>
            ) : (
              wallets.filter(w => w.isActive !== false).map((wallet) => (
                <tr key={wallet._id} className="hover:bg-gray-50 dark:bg-gray-800/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{wallet.label}</div>
                    {wallet.notes && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{wallet.notes}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {wallet.enabledChains.map((chain) => (
                        <span
                          key={chain}
                          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getChainBadge(chain)}`}
                        >
                          {getChainLabel(chain)}
                        </span>
                      ))}
                    </div>
                    {wallet.enabledChains?.includes('evm') && (
                      <div className="text-xs text-gray-500 mt-1">
                        {EVM_NETWORK_LABELS.join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {wallet.enabledChains.map((chain) => (
                        <div key={chain} className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">{getChainLabel(chain)}:</span>{' '}
                          <span className="font-mono">
                            {truncateAddress(wallet.chains?.[chain]?.address)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        wallet.isActive
                          ? 'bg-green-100 text-green-800 dark:text-green-300'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {wallet.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(wallet.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => viewWallet(wallet._id)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Wallet Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Trading Wallet</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Label</label>
                  <input
                    type="text"
                    value={createForm.label}
                    onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                    placeholder="e.g. Trading Bot #1"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Chains</label>
                  <div className="grid grid-cols-1 gap-2">
                    {CHAIN_OPTIONS.map((chain) => (
                      <label
                        key={chain.id}
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                          createForm.chains.includes(chain.id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.chains.includes(chain.id)}
                          onChange={() => toggleChain(chain.id)}
                          className="mr-2 mt-0.5"
                        />
                        <span>
                          <span className="text-sm font-medium block">{chain.label}</span>
                          {chain.hint && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{chain.hint}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    placeholder="Optional notes about this wallet..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={createWallet}
                    disabled={creating}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Wallet'}
                  </button>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Wallet Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Import Trading Wallet</h2>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Label</label>
                  <input
                    type="text"
                    value={importForm.label}
                    onChange={(e) => setImportForm({ ...importForm, label: e.target.value })}
                    placeholder="e.g. Imported Wallet #1"
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Chains</label>
                  <div className="grid grid-cols-1 gap-2">
                    {CHAIN_OPTIONS.map((chain) => (
                      <label
                        key={chain.id}
                        className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                          importForm.chains.includes(chain.id)
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={importForm.chains.includes(chain.id)}
                          onChange={() => toggleImportChain(chain.id)}
                          className="mr-2 mt-0.5"
                        />
                        <span>
                          <span className="text-sm font-medium block">{chain.label}</span>
                          {chain.hint && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">{chain.hint}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {importForm.chains.includes('solana') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Solana Private Key
                    </label>
                    <input
                      type="password"
                      value={importForm.solanaPrivateKey}
                      onChange={(e) => setImportForm({ ...importForm, solanaPrivateKey: e.target.value })}
                      placeholder="Base58 key, [12,34,…] byte array, or hex"
                      autoComplete="off"
                      spellCheck={false}
                      className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Accepts Phantom/Solflare base58, Solana CLI byte array, or hex.
                    </p>
                  </div>
                )}

                {importForm.chains.includes('evm') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      EVM Private Key
                    </label>
                    <input
                      type="password"
                      value={importForm.evmPrivateKey}
                      onChange={(e) => setImportForm({ ...importForm, evmPrivateKey: e.target.value })}
                      placeholder="0x… (64-char hex)"
                      autoComplete="off"
                      spellCheck={false}
                      className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      One key for Base, Ethereum, BSC & Robinhood (same address on all).
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={importForm.notes}
                    onChange={(e) => setImportForm({ ...importForm, notes: e.target.value })}
                    placeholder="Optional notes about this wallet..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={2}
                    maxLength={500}
                  />
                </div>

                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Private keys are encrypted at rest and never returned by the API. Only import keys you control.
                </p>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={importWallet}
                    disabled={importing}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {importing ? 'Importing...' : 'Import Wallet'}
                  </button>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Detail Modal */}
      {showDetailModal && selectedWallet && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedWallet.label}</h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedWallet(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              {selectedWallet.notes && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{selectedWallet.notes}</p>
              )}

              {/* Chain addresses */}
              <div className="space-y-3 mb-6">
                <h3 className="text-lg font-semibold">Chain Addresses</h3>
                {selectedWallet.enabledChains.map((chain) => (
                  <div
                    key={chain}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                  >
                    <div className="flex-1">
                      <span
                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getChainBadge(chain)}`}
                      >
                        {getChainLabel(chain)}
                      </span>
                      {chain === 'evm' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Shared across {EVM_NETWORK_LABELS.join(' · ')}
                        </p>
                      )}
                      <p className="font-mono text-sm mt-1 break-all">
                        {selectedWallet.chains?.[chain]?.address || 'N/A'}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() =>
                          copyToClipboard(selectedWallet.chains?.[chain]?.address || '')
                        }
                        className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => openExportModal(chain)}
                        className="px-3 py-1 text-xs bg-red-100 text-red-700 dark:text-red-400 rounded hover:bg-red-200"
                      >
                        Export Key
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Wallet Info */}
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="text-gray-500">Status</p>
                  <span
                    className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                      selectedWallet.isActive
                        ? 'bg-green-100 text-green-800 dark:text-green-300'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {selectedWallet.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500">Created By</p>
                  <p className="font-medium">{selectedWallet.createdBy}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">
                    {new Date(selectedWallet.createdAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Updated</p>
                  <p className="font-medium">
                    {new Date(selectedWallet.updatedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Portfolio Breakdown */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Portfolio</h3>
                {portfolioLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading portfolio...</p>
                ) : portfolio ? (
                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{portfolio.activePositions}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Closed</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{portfolio.closedPositions}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Invested</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{portfolio.totalInvested.toFixed(4)}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Win Rate</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">{portfolio.winRate}%</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Realized</p>
                        <p className={`text-lg font-bold ${portfolio.totalRealized >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {portfolio.totalRealized >= 0 ? '+' : ''}{portfolio.totalRealized.toFixed(4)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Unrealized</p>
                        <p className={`text-lg font-bold ${portfolio.unrealizedPnl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {portfolio.unrealizedPnl >= 0 ? '+' : ''}{portfolio.unrealizedPnl.toFixed(4)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total P&L</p>
                        <p className={`text-lg font-bold ${portfolio.totalPnl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {portfolio.totalPnl >= 0 ? '+' : ''}{portfolio.totalPnl.toFixed(4)}
                          <span className="text-xs ml-1">({portfolio.totalPnlPercent >= 0 ? '+' : ''}{portfolio.totalPnlPercent}%)</span>
                        </p>
                      </div>
                    </div>
                    {portfolio.positions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase">Positions</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {portfolio.positions.map((pos) => (
                            <div key={pos._id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 rounded px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${pos.status === 'active' ? 'bg-green-500' : pos.status === 'closed' ? 'bg-gray-400' : 'bg-yellow-500'}`} />
                                <span className="font-medium text-gray-900 dark:text-white">{pos.tokenSymbol}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{pos.chain}</span>
                                {pos.executionMode === 'live' && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded font-semibold">LIVE</span>
                                )}
                              </div>
                              <span className={`font-medium ${(pos.totalPnl || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {(pos.totalPnl || 0) >= 0 ? '+' : ''}{(pos.totalPnl || 0).toFixed(4)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No portfolio data available</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t dark:border-gray-700">
                <button
                  onClick={openEditModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleWalletStatus(selectedWallet._id)}
                  className={`px-4 py-2 text-white rounded ${
                    selectedWallet.isActive
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {selectedWallet.isActive ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => deleteWallet(selectedWallet._id)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Edit Wallet</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Label</label>
                  <input
                    type="text"
                    value={editForm.label}
                    onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    maxLength={500}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={saveEdit}
                    disabled={editing}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editing ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Key Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-red-600 mb-2">Export Private Key</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                You are about to export the <strong>{getChainLabel(exportChain)}</strong> private key
                for <strong>{selectedWallet?.label}</strong>. This action is logged for security.
              </p>

              {!exportedKey ? (
                <div className="flex gap-3">
                  <button
                    onClick={exportKey}
                    disabled={exporting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {exporting ? 'Decrypting...' : 'Confirm Export'}
                  </button>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-red-50 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
                    <p className="font-mono text-sm break-all select-all">{exportedKey}</p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => copyToClipboard(exportedKey)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Copy to Clipboard
                    </button>
                    <button
                      onClick={() => {
                        setExportedKey('');
                        setShowExportModal(false);
                      }}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
